import { getWordCount, getWordIndexes, inputWordLimitCheck } from '@Common/handlers/helper';
import { Theme } from '@quillbot/design-system';
import { CHAR_LIMITS } from 'config/constants';
import { splitIntoSentences } from 'core/utils/sentenceSplitter';
import Statistics from 'core/utils/statistics';
import {
  CitationSpanClassName,
  ElementsToSkipForGrammarChecker,
} from 'src/researcher/constants/common.constants';
import QuillEditor, { QuillEditorIDS } from 'src/_common/handlers/quillEditor';
import { Editor } from 'tinymce';
import { IGrammarCheckerResponse, IReplacementObject, Suggestion } from '../models/api';
import { ICustomNodeObject, ITopLevelCustomNodeObject } from '../models/GrammarCheck';

const statsCalculator = Statistics.getInstance();

export const specialCharacters = {
  ',': 'Comma',
  '!': 'Exclamation Mark',
  "'": 'Apostrophe',
  '?': 'Question Mark',
  '.': 'Period',
};

let timer: any = null;

export const FIX_ALL_GRAMMAR_ERRORS_EVENT_NAME = 'FIX_ALL_GRAMMAR_ERRORS_EVENT_NAME';

const blockRegex =
  /^(address|blockquote|body|center|dir|div|dl|fieldset|form|h[1-6]|hr|isindex|menu|noframes|noscript|ol|p|pre|table|ul|dd|dt|frameset|li|tbody|td|tfoot|th|thead|tr|html)$/i;

export const isBlockLevelElement = (nodeName: string) => blockRegex.test(nodeName);

export const CONSTANTS = Object.freeze({
  GRAMMAR_BOT_ID: 'grammarbot',
  ERROR_UNDERLINE_CLASS_NAME: 'red-underline',
  PARAPHRASE_UNDERLINE_CLASS_NAME: 'blue-underline',
  GRAMMAR_CHECKER_BATCH_SIZE: 4,
  GRAMMAR_CHECKER_BATCHING_LIMIT: 150,
});
export enum UnderlinedClassnames {
  ERROR_UNDERLINE_CLASS_NAME = 'red-underline',
  PARAPHRASE_UNDERLINE_CLASS_NAME = 'blue-underline',
  COMPLEX_UNDERLINE_CLASS_NAME = 'blue-complex-underline',
}
export const createUnderlinedNodeHTML = (
  nextErrorCharacters: string,
  contenntToReplaceObject: IReplacementObject,
  paragraphId: string,
  className?: string
) =>
  `<span class="${
    className || UnderlinedClassnames.ERROR_UNDERLINE_CLASS_NAME
  }" data-startIndex=${contenntToReplaceObject.startIndex.toString()} data-endIndex=${contenntToReplaceObject.endIndex.toString()} data-paragraphid=${paragraphId} >${nextErrorCharacters}</span>`;

export const setCurrentCursorPosition = (mainNode: Node, char: number) => {
  function createRange(
    node: Node,
    chars: { count: number },
    range?: Range | undefined
  ): Range | undefined {
    if (!range) {
      range = document.createRange();
      range.selectNode(node);
      range.setStart(node, 0);
    }

    if (chars.count === 0) {
      range.setEnd(node, chars.count);
    } else if (node !== undefined && chars.count > 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        if ((node.textContent as string).length < chars.count) {
          chars.count -= (node.textContent as string).length as number;
        } else {
          range.setEnd(node, chars.count);
          chars.count = 0;
        }
      } else {
        for (let lp = 0; lp < node.childNodes.length; lp += 1) {
          range = createRange(node.childNodes[lp], chars, range);

          if (chars.count === 0) {
            break;
          }
        }
      }
    }
    return range;
  }

  if (char >= 0) {
    const selection = window.getSelection();
    if (!mainNode) return;
    const range = createRange(mainNode, {
      count: char,
    });
    if (range) {
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
};

export const getCaretPosition = (parentNode: Node) => {
  const element = parentNode;
  let caretOffset = 0;
  if (typeof window.getSelection !== 'undefined') {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      if (element) {
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
      }
    } else {
      caretOffset = 0;
    }
  } else if (document.getSelection()?.type !== 'Control') {
    const textRange = document.selection.createRange();
    const preCaretTextRange = document.body.createTextRange();
    preCaretTextRange.moveToElementText(element);
    preCaretTextRange.setEndPoint('EndToEnd', textRange);
    caretOffset = preCaretTextRange.text.length;
  }
  return caretOffset + 1;
};

export const getChildNodes = (
  customNodesObjectArray: ICustomNodeObject[],
  node: Node,
  startIndexObject: { value: number }
) => {
  if (node.nodeName === '#comment') {
    return;
  }
  if (node.nodeType === Node.TEXT_NODE && node.childNodes.length === 0) {
    const length = node.textContent?.split('').length as number;
    const endIndex = startIndexObject.value + length - 1;
    customNodesObjectArray.push({
      startIndex: startIndexObject.value,
      endIndex,
      node,
    });
    startIndexObject.value = endIndex + 1;
    return;
  }
  node.childNodes.forEach((childNode: Node) => {
    getChildNodes(customNodesObjectArray, childNode, startIndexObject);
  });
};

export const unwrapErrorNode = (
  spanErrorNode: HTMLElement,
  editorId: string,
  shouldUpdateCursorPosition: boolean,
  isEnterPressed?: boolean
) => {
  const currentCursorPosition = getCaretPosition(document.getElementById(editorId) as Node);
  const docFrag = document.createDocumentFragment();
  // const ignoredDatasetValue = spanErrorNode.dataset.ignored;
  while (spanErrorNode.firstChild) {
    const child = spanErrorNode.removeChild(spanErrorNode.firstChild);
    docFrag.appendChild(child);
  }
  const replaceWordsLength = docFrag.textContent?.length || 0;
  // replace wrapper with document fragment
  // const parentSpanNode = document.createElement('span');
  // parentSpanNode.setAttribute('data-ignored', ignoredDatasetValue || '');
  // parentSpanNode.appendChild(docFrag);
  spanErrorNode.parentNode?.replaceChild(docFrag, spanErrorNode);

  if (shouldUpdateCursorPosition) {
    setCurrentCursorPosition(
      document.getElementById(editorId) as Node,
      currentCursorPosition + replaceWordsLength - 1
    );
  } else if (!isEnterPressed) {
    setCurrentCursorPosition(document.getElementById(editorId) as Node, currentCursorPosition - 1);
  }
};

export const removeEmptyParagraphNodesFromBR = (
  trackFn: null | ((text: string, numberOfWords: number) => void),
  debounceFn: () => void,
  isMobile: boolean,
  editorName: QuillEditorIDS
) => {
  setTimeout(() => {
    let shouldScroll = false;
    Array.from(document.getElementsByClassName('div-to-remove')).forEach((div) => {
      shouldScroll = true;
      if (isMobile) {
        div.innerHTML = '';
      } else {
        div.parentNode?.removeChild(div);
      }
    });
    if (shouldScroll) {
      window.scroll({
        top: (document.getElementById('grammarbot')?.clientHeight as number) - 500,
        behavior: 'smooth',
      });
    }
    debounceFn();
    trackFn?.(
      QuillEditor.getInstance(editorName)?.getContent('text') as string,
      QuillEditor.getInstance(editorName)?.getWordCount() as number
    );
  }, 0);
};

export const recurseThroughNodes = (node: Node, firstLevel = true) => {
  if (!firstLevel && isBlockLevelElement(node.nodeName)) {
    const spanNode = document.createElement('span');
    spanNode.innerHTML = (node as HTMLElement).innerHTML;
    node.parentNode?.replaceChild(spanNode, node);
    node.parentNode?.removeChild(node);
  }

  const nodeName = node.nodeName;
  if (nodeName === 'BR') {
    const divNode = document.createElement('div');
    divNode.className = 'div-to-remove';
    node.parentNode?.replaceChild(divNode, node);
  }
  if (nodeName === 'UL' || nodeName === 'OL') {
    const nodesFragment = document.createDocumentFragment();
    node.childNodes.forEach((liNode: Node, index) => {
      const newListParent = document.createElement(nodeName.toLowerCase());
      newListParent.classList.add('reduce-margin-block-end');
      if (nodeName === 'OL') {
        if ((node as HTMLOListElement).type) {
          newListParent.setAttribute('type', (node as HTMLOListElement).type);
        }
        newListParent.setAttribute('start', (index + (node as HTMLOListElement).start).toString());
      }
      const clonedLiNode = liNode.cloneNode(true);
      newListParent.appendChild(clonedLiNode);
      nodesFragment.appendChild(newListParent);
    });
    (node as HTMLElement).parentNode?.replaceChild(nodesFragment, node);
  }

  if (node.childNodes.length === 0) {
    return;
  }
  Array.from((node as HTMLElement).attributes).forEach((attr: Attr) => {
    (node as HTMLElement).removeAttribute(attr.name);
  });

  node.childNodes.forEach((childNode: Node) => {
    recurseThroughNodes(childNode, false);
  });
};

export const removeAllErrorMarkersAndHandlers = (
  errorClassName: string,
  editorId: string,
  isEnterPressed: boolean,
  paragraph?: Node
) => {
  let previousErrorNodes: Element[];
  if (paragraph !== undefined) {
    previousErrorNodes = Array.from(
      (paragraph as HTMLElement).querySelectorAll(
        `.${UnderlinedClassnames.ERROR_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.PARAPHRASE_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.COMPLEX_UNDERLINE_CLASS_NAME}`
      )
    ) as HTMLElement[];
  } else {
    previousErrorNodes = Array.from(
      document.querySelectorAll(
        `.${UnderlinedClassnames.ERROR_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.PARAPHRASE_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.COMPLEX_UNDERLINE_CLASS_NAME}`
      )
    );
  }
  previousErrorNodes.forEach((errorNode) => {
    unwrapErrorNode(errorNode as HTMLElement, editorId, false, isEnterPressed);
  });
};

export const directlyFixGrammarErrorHandler = (
  doc: HTMLElement,
  wordReplacement: string,
  editorId: string
) => {
  doc.innerText = wordReplacement;
  unwrapErrorNode(doc, editorId, true);
};

export const getSentenceHavingWord = (
  paragraph: string,
  word: string,
  wordStartIndex: number,
  wordEndIndex: number
): string => {
  const sentences = splitIntoSentences(paragraph).filter(
    (sentence) => sentence.type === 'Sentence'
  );
  let currentIndex = 0;
  let matchedSentence = '';
  for (let i = 0; i < sentences.length; i += 1) {
    // @TODO handle cases where error string lies in 2 sentences
    const sentence = sentences[i].raw;
    if (
      wordStartIndex >= currentIndex &&
      wordEndIndex <= currentIndex + sentence.length - 1 &&
      sentence.includes(word)
    ) {
      matchedSentence = sentence;
      break;
    }
    currentIndex += sentence.length;
  }
  return matchedSentence;
};

const getUnderlineClassNames = (
  contentToReplace: IReplacementObject[],
  currentReplacementObjectIndex: number,
  gcFluencyModeEnabled: boolean
) => {
  if (
    contentToReplace[currentReplacementObjectIndex].isFluencyModeRequired &&
    gcFluencyModeEnabled &&
    contentToReplace[currentReplacementObjectIndex].isSentenceComplex
  ) {
    return `${UnderlinedClassnames.PARAPHRASE_UNDERLINE_CLASS_NAME} ${UnderlinedClassnames.COMPLEX_UNDERLINE_CLASS_NAME}`;
  }
  if (
    contentToReplace[currentReplacementObjectIndex].isFluencyModeRequired &&
    gcFluencyModeEnabled
  ) {
    return UnderlinedClassnames.PARAPHRASE_UNDERLINE_CLASS_NAME;
  }
  if (contentToReplace[currentReplacementObjectIndex].isSentenceComplex) {
    return UnderlinedClassnames.COMPLEX_UNDERLINE_CLASS_NAME;
  }
  return UnderlinedClassnames.ERROR_UNDERLINE_CLASS_NAME;
};

export const applyGrammarResponse = ({
  topLevelCustomNodesObject,
  topLevelParagraphNode,
  res,
  editorId,
  setAnchorEl,
  setErrorHoverState,
  setIsGrammarCheckRunning,
  setHasGrammarErrors,
  setChangeByKey,
  ignoredWords,
  removeAllErrorMarkersAndHandlersAfterParaphrase,
  gcFluencyModeEnabled,
}: {
  topLevelCustomNodesObject: ITopLevelCustomNodeObject;
  topLevelParagraphNode: Node;
  res: IGrammarCheckerResponse;
  editorId: string;
  setAnchorEl: (doc: HTMLElement, replacementObject: IReplacementObject, sentence: string) => void;
  setErrorHoverState: (value: boolean) => void;
  setIsGrammarCheckRunning: (isGrammarCheckRunning: boolean) => void;
  setHasGrammarErrors: () => void;
  setChangeByKey: (value: boolean) => void;
  ignoredWords: { startIndex: number; endIndex: number; word: string; paragraphId: string }[];
  removeAllErrorMarkersAndHandlersAfterParaphrase: Function;
  gcFluencyModeEnabled: boolean;
}): boolean => {
  // let loopCount = 1;
  const currentCursorPosition = getCaretPosition(document.getElementById(editorId) as Node);
  try {
    const { customNodesObjectArray } = topLevelCustomNodesObject;

    const currentNodeIdFromDataAttr = (topLevelParagraphNode as HTMLElement).dataset.paragraphid;

    let { totalCharactersProcessedCount, totalNodesProcessedCount, currentReplacementObjectIndex } =
      topLevelCustomNodesObject;

    const { contentToReplace } = res;

    removeAllErrorMarkersAndHandlersAfterParaphrase();
    // setIsGrammarCheckRunning(false);

    if (contentToReplace.length === 0) {
      setChangeByKey(false);
      setHasGrammarErrors();
      return true;
    }

    for (
      let currentNodeIndex = totalNodesProcessedCount;
      currentNodeIndex < customNodesObjectArray.length;
      currentNodeIndex = totalNodesProcessedCount
    ) {
      let completeText = '';
      let wasCurrentNodeProcessed = false;
      while (true) {
        // for ignore words
        for (let i = 0; i < ignoredWords.length; i += 1) {
          if (
            contentToReplace[currentReplacementObjectIndex] &&
            contentToReplace[currentReplacementObjectIndex].startIndex ===
              ignoredWords[i].startIndex &&
            contentToReplace[currentReplacementObjectIndex].endIndex === ignoredWords[i].endIndex &&
            contentToReplace[currentReplacementObjectIndex].originalWord === ignoredWords[i].word &&
            parseInt(currentNodeIdFromDataAttr || Number.MAX_SAFE_INTEGER.toString(), 10) ===
              parseInt(ignoredWords[i].paragraphId, 10)
          ) {
            contentToReplace.splice(currentReplacementObjectIndex, 1);
            break;
          }
        }
        // for breaking out
        if (
          !contentToReplace[currentReplacementObjectIndex] ||
          contentToReplace[currentReplacementObjectIndex].startIndex >
            customNodesObjectArray[currentNodeIndex].endIndex
        ) {
          if (!wasCurrentNodeProcessed) {
            totalNodesProcessedCount += 1;
            totalCharactersProcessedCount += customNodesObjectArray[
              currentNodeIndex
            ].node.textContent?.split('').length as number;
          }
          break;
        }
        wasCurrentNodeProcessed = true;
        let splittedInputTextArray = customNodesObjectArray[
          totalNodesProcessedCount
        ].node.textContent?.split('') as string[];
        for (
          let currentCharacterIndex = totalCharactersProcessedCount;
          currentCharacterIndex < totalCharactersProcessedCount + splittedInputTextArray.length;
          currentCharacterIndex += 1
        ) {
          // loopCount += 1;
          // if (loopCount > 40000) {
          //   console.log('dummy infinite loop condition found, breaking out');
          //   break;
          // }
          if (
            contentToReplace[currentReplacementObjectIndex] &&
            currentCharacterIndex < contentToReplace[currentReplacementObjectIndex].startIndex
          ) {
            completeText += `${
              splittedInputTextArray[currentCharacterIndex - totalCharactersProcessedCount]
            }`;
            if (
              currentCharacterIndex === customNodesObjectArray[totalNodesProcessedCount].endIndex
            ) {
              totalCharactersProcessedCount += splittedInputTextArray.length;
              totalNodesProcessedCount += 1;
              splittedInputTextArray = [];
            }
          } else if (
            // handling multiple/single replace operations in same node
            contentToReplace[currentReplacementObjectIndex] &&
            currentCharacterIndex === contentToReplace[currentReplacementObjectIndex].startIndex &&
            contentToReplace[currentReplacementObjectIndex].endIndex <=
              customNodesObjectArray[currentNodeIndex].endIndex
          ) {
            let nextErrorCharacters = '';
            while (
              currentCharacterIndex <= contentToReplace[currentReplacementObjectIndex].endIndex &&
              currentCharacterIndex < totalCharactersProcessedCount + splittedInputTextArray.length
            ) {
              nextErrorCharacters += `${
                splittedInputTextArray[currentCharacterIndex - totalCharactersProcessedCount]
              }`;
              if (
                currentCharacterIndex === contentToReplace[currentReplacementObjectIndex].endIndex
              ) {
                if (
                  currentCharacterIndex ===
                  customNodesObjectArray[totalNodesProcessedCount].endIndex
                ) {
                  totalCharactersProcessedCount += splittedInputTextArray.length;
                  totalNodesProcessedCount += 1;
                  splittedInputTextArray = [];
                }
                break;
              }
              currentCharacterIndex += 1;
            }
            const nextErrorHTML = createUnderlinedNodeHTML(
              nextErrorCharacters,
              contentToReplace[currentReplacementObjectIndex],
              currentNodeIdFromDataAttr as string,
              getUnderlineClassNames(
                contentToReplace,
                currentReplacementObjectIndex,
                gcFluencyModeEnabled
              )
            );
            completeText += nextErrorHTML;
            currentReplacementObjectIndex += 1;
          } else if (
            contentToReplace[currentReplacementObjectIndex] &&
            contentToReplace[currentReplacementObjectIndex].startIndex <=
              customNodesObjectArray[currentNodeIndex].endIndex &&
            contentToReplace[currentReplacementObjectIndex].endIndex >
              customNodesObjectArray[currentNodeIndex].endIndex &&
            currentCharacterIndex >= contentToReplace[currentReplacementObjectIndex].startIndex
          ) {
            // to handle replace operations spanning into multiple nodes
            let nextErrorCharacters = '';
            let nextCompleteText = '';
            // loop through current words array
            while (
              currentCharacterIndex <
              totalCharactersProcessedCount + splittedInputTextArray.length
            ) {
              nextErrorCharacters += `${
                splittedInputTextArray[currentCharacterIndex - totalCharactersProcessedCount]
              }`;
              currentCharacterIndex += 1;
            }
            totalNodesProcessedCount += 1;
            totalCharactersProcessedCount += splittedInputTextArray.length;

            // for looping through multiple customNodeObj
            while (totalNodesProcessedCount < customNodesObjectArray.length) {
              const nextSplittedInputTextArray = customNodesObjectArray[
                totalNodesProcessedCount
              ].node.textContent?.split('') as string[];
              if (
                contentToReplace[currentReplacementObjectIndex].endIndex <
                totalCharactersProcessedCount + nextSplittedInputTextArray.length
              ) {
                // next error words lies in next customNodeObj only
                for (
                  let nextWordsCharacterIndex = totalCharactersProcessedCount;
                  nextWordsCharacterIndex <
                  totalCharactersProcessedCount + nextSplittedInputTextArray.length;
                  nextWordsCharacterIndex += 1
                ) {
                  if (
                    nextWordsCharacterIndex ===
                      contentToReplace[currentReplacementObjectIndex].endIndex &&
                    nextWordsCharacterIndex ===
                      customNodesObjectArray[totalNodesProcessedCount].endIndex
                  ) {
                    nextErrorCharacters += `${
                      nextSplittedInputTextArray[
                        nextWordsCharacterIndex - totalCharactersProcessedCount
                      ]
                    }`;
                    if (
                      !customNodesObjectArray[
                        totalNodesProcessedCount
                      ].node.parentElement?.className.match(`${CitationSpanClassName}`)
                    ) {
                      customNodesObjectArray[totalNodesProcessedCount].node.textContent = '';
                    }
                    totalNodesProcessedCount += 1;
                    totalCharactersProcessedCount += nextSplittedInputTextArray.length;
                    currentCharacterIndex = totalCharactersProcessedCount;
                    splittedInputTextArray = [];
                    break;
                  } else if (
                    nextWordsCharacterIndex <=
                    contentToReplace[currentReplacementObjectIndex].endIndex
                  ) {
                    nextErrorCharacters += `${
                      nextSplittedInputTextArray[
                        nextWordsCharacterIndex - totalCharactersProcessedCount
                      ]
                    }`;
                  } else {
                    currentCharacterIndex = nextWordsCharacterIndex - 1;
                    if (
                      !customNodesObjectArray[
                        totalNodesProcessedCount
                      ].node.parentElement?.className.match(`${CitationSpanClassName}`)
                    ) {
                      customNodesObjectArray[totalNodesProcessedCount].node.textContent =
                        nextSplittedInputTextArray
                          .slice(nextWordsCharacterIndex - totalCharactersProcessedCount)
                          .join('');
                    }
                    totalCharactersProcessedCount = nextWordsCharacterIndex;
                    splittedInputTextArray = [];
                    break;
                  }
                }
                nextCompleteText = createUnderlinedNodeHTML(
                  nextErrorCharacters,
                  contentToReplace[currentReplacementObjectIndex],
                  currentNodeIdFromDataAttr as string,
                  getUnderlineClassNames(
                    contentToReplace,
                    currentReplacementObjectIndex,
                    gcFluencyModeEnabled
                  )
                );
                completeText += nextCompleteText;
                currentReplacementObjectIndex += 1;
                break;
              } else {
                // next error words spanning into next multiple customNodeObj
                for (
                  let nextWordsCharacterIndex = totalCharactersProcessedCount;
                  nextWordsCharacterIndex <
                  totalCharactersProcessedCount + nextSplittedInputTextArray.length;
                  nextWordsCharacterIndex += 1
                ) {
                  nextErrorCharacters += `${
                    nextSplittedInputTextArray[
                      nextWordsCharacterIndex - totalCharactersProcessedCount
                    ]
                  }`;
                }
                if (
                  !customNodesObjectArray[
                    totalNodesProcessedCount
                  ].node.parentElement?.className.match(`${CitationSpanClassName}`)
                ) {
                  customNodesObjectArray[totalNodesProcessedCount].node.textContent = '';
                }
                totalNodesProcessedCount += 1;
                totalCharactersProcessedCount += nextSplittedInputTextArray.length;
              }
            }
          } else {
            /* Tag: Last Else Condition */
            // Process character after error words (normal characters)
            // until the next error word or the current node completion

            splittedInputTextArray = customNodesObjectArray[
              totalNodesProcessedCount
            ].node.textContent?.split('') as string[];
            completeText += `${
              splittedInputTextArray[currentCharacterIndex - totalCharactersProcessedCount]
            }`;

            if (
              currentCharacterIndex === customNodesObjectArray[totalNodesProcessedCount].endIndex
            ) {
              if (
                !customNodesObjectArray[
                  totalNodesProcessedCount
                ].node.parentElement?.className.match(`${CitationSpanClassName}`)
              ) {
                customNodesObjectArray[totalNodesProcessedCount].node.textContent = '';
              }
              totalNodesProcessedCount += 1;
              totalCharactersProcessedCount += splittedInputTextArray.length;
              splittedInputTextArray = [];
            }
          }
        }
        const newNodeWithErrorStyle = document.createElement('span');
        newNodeWithErrorStyle.innerHTML = completeText;
        // console.log((customNodesObjectArray[currentNodeIndex].node as HTMLElement).className);
        // console.log(
        //   customNodesObjectArray,
        //   customNodesObjectArray[currentNodeIndex].node.parentNode,
        //   customNodesObjectArray[currentNodeIndex].node
        // );

        if (
          !customNodesObjectArray[currentNodeIndex].node.parentElement?.className.match(
            `${CitationSpanClassName}`
          )
        ) {
          (customNodesObjectArray[currentNodeIndex].node.parentNode as HTMLElement)?.replaceChild(
            newNodeWithErrorStyle,
            customNodesObjectArray[currentNodeIndex].node
          );
        }
        totalCharactersProcessedCount += splittedInputTextArray.length;
      }
    }

    setCurrentCursorPosition(document.getElementById(editorId) as Node, currentCursorPosition - 1);

    const errorNodes: HTMLElement[] = Array.from(
      (topLevelParagraphNode as HTMLElement).querySelectorAll(
        `.${UnderlinedClassnames.ERROR_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.PARAPHRASE_UNDERLINE_CLASS_NAME}, .${UnderlinedClassnames.COMPLEX_UNDERLINE_CLASS_NAME}`
      )
    );

    const openCorrectionCard = (event: MouseEvent, errorNode: HTMLElement, index: number) => {
      const immediatePorDivParentElement =
        topLevelCustomNodesObject.topLevelParagraphNode as HTMLElement;
      let sentenceHavingWord = getSentenceHavingWord(
        immediatePorDivParentElement.innerText,
        errorNode.innerText,
        contentToReplace[index].startIndex,
        contentToReplace[index].endIndex
      );
      if (sentenceHavingWord.trim().length <= 0) sentenceHavingWord = errorNode.innerText;
      // setErrorHoverState(false);
      setAnchorEl(errorNode, contentToReplace[index], sentenceHavingWord);
      setErrorHoverState(true);
      // if (event.type === 'mouseenter') {
      //   setErrorClickState(false);
      // }
      // if (event.type === 'click') setErrorClickState(true);
      event.stopPropagation();
      clearTimeout(timer);
    };

    errorNodes.forEach((errorNode, index) => {
      errorNode.addEventListener('mouseenter', (e: MouseEvent) => {
        openCorrectionCard(e, errorNode, index);
      });

      // errorNode.addEventListener('click', (e: MouseEvent) => {
      //   openCorrectionCard(e, errorNode, index);
      // });

      errorNode.addEventListener('mouseleave', () => {
        timer = setTimeout(() => {
          setErrorHoverState(false);
        }, 400);
      });

      errorNode.addEventListener(FIX_ALL_GRAMMAR_ERRORS_EVENT_NAME, () => {
        if (
          !contentToReplace[index].isSentenceComplex ||
          contentToReplace[index].isFluencyModeRequired
        ) {
          directlyFixGrammarErrorHandler(
            errorNode,
            (contentToReplace[index].replacementWord[0] as Suggestion).suggestion,
            editorId
          );
        }
      });
    });

    setChangeByKey(false);
    setHasGrammarErrors();
    // Promise.resolve();
  } catch (e) {
    removeAllErrorMarkersAndHandlers(
      CONSTANTS.ERROR_UNDERLINE_CLASS_NAME,
      QuillEditorIDS.RESEARCH_EDITOR,
      false
    );
    setChangeByKey(false);
    setHasGrammarErrors();
    console.log('Error occure while applying grammar checks', e);
    return false;
  }
  return true;
};

export const createTopLevelCustomNodesObjectArray = (
  // editorId: string
  updatedNodes: Node[] | NodeList,
  splitIndex = 0
): ITopLevelCustomNodeObject[] => {
  const topLevelCustomNodesObjectArray: ITopLevelCustomNodeObject[] = [];
  updatedNodes.forEach((topLevelParagraphNode: Node, index: number) => {
    if (ElementsToSkipForGrammarChecker?.length) {
      // making it optional so it doesn't query on text nodes
      // (for co-writer: pagination)
      (topLevelParagraphNode as HTMLElement)
        .querySelectorAll?.(ElementsToSkipForGrammarChecker.join(', '))
        .forEach((node) => {
          node.parentNode?.removeChild(node);
        });
    }
    let topLevelNodeModifiedTextContent = topLevelParagraphNode.textContent
      ?.replace(/‘|’/g, "'")
      .replace(/\u00a0/g, ' ')
      .replace(/“|”/g, '"');

    /* Added the new attribute for the paras for which some text is under limit and some text not,
        For these paras we are taking the allowed text only and pushed it to the modified text to handle api call accordingly */
    // making it optional so it doesn't query on text nodes
    // (for co-writer: pagination)
    if ((topLevelParagraphNode as HTMLElement).getAttribute?.('limiter') === '2') {
      topLevelNodeModifiedTextContent = topLevelNodeModifiedTextContent?.substring(0, splitIndex);
    }
    topLevelCustomNodesObjectArray.push({
      customNodesObjectArray: [],
      globalNodeStartIndex: { value: 0 },
      totalCharactersProcessedCount: 0,
      totalNodesProcessedCount: 0,
      currentReplacementObjectIndex: 0,
      originalText: topLevelParagraphNode.textContent || '',
      modifiedText: topLevelNodeModifiedTextContent || '',
      topLevelParagraphNode,
    });

    getChildNodes(
      topLevelCustomNodesObjectArray[index].customNodesObjectArray,
      topLevelParagraphNode,
      topLevelCustomNodesObjectArray[index].globalNodeStartIndex
    );
  });
  return topLevelCustomNodesObjectArray;
};

export const getBottomBorderClass = (isGrammarCheckRunning: boolean, hasGrammarErrors: boolean) => {
  if (isGrammarCheckRunning) {
    return 'grammarbot-container-bottom-border-processing';
  }
  if (hasGrammarErrors) {
    return 'grammarbot-container-bottom-border-error';
  }
  return 'grammarbot-container-bottom-border-correct';
};

const config = { childList: true, subtree: true, characterData: true };

export const startObservingRootNodes = (targetNode: any, callback: any) => {
  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);
};

export const allSettled = <T>(promises: Promise<T>[]) => {
  const wrappedPromises = promises.map((p) =>
    Promise.resolve(p)
      .then((value) => ({ status: 'fulfilled', value }))
      .catch((err) => ({ status: 'rejected', reason: err }))
  );
  return Promise.all(wrappedPromises);
};

export const charLimitChecker = (unsafeEditorInstance: Editor | undefined, theme: Theme) => {
  let wordCount = 0;
  let limiterValue = 2;
  let lastParaWordCount = 0;
  let splitIndex = 0;
  unsafeEditorInstance?.getBody().childNodes.forEach((childNode: any) => {
    const node =
      childNode.nodeName === '#text'
        ? (childNode.parentNode as HTMLElement)
        : (childNode as HTMLElement);

    wordCount += getWordCount(node.textContent as string);

    // if (wordCount === CHAR_LIMITS.GRAMMAR_CHECKER_WORD_LIMIT) limiterValue = 1;

    if (wordCount >= CHAR_LIMITS.GRAMMAR_CHECKER_WORD_LIMIT) {
      node.setAttribute('limiter', `${limiterValue}`);

      if (limiterValue !== 2) node.style.color = '#818181';
      else {
        const wordIndexes = getWordIndexes(node.textContent || '');
        lastParaWordCount = wordCount - getWordCount(wordIndexes);
        const currentCursorPosition = getCaretPosition(
          document.getElementById(CONSTANTS.GRAMMAR_BOT_ID) as Node
        );

        splitIndex = inputWordLimitCheck(
          wordIndexes,
          CHAR_LIMITS.GRAMMAR_CHECKER_WORD_LIMIT - lastParaWordCount
        );

        node.innerHTML = `${node.textContent?.substring(
          0,
          splitIndex
        )}<span style="color:#818181">${node.textContent?.substring(splitIndex)}</span>`;

        setCurrentCursorPosition(
          document.getElementById(CONSTANTS.GRAMMAR_BOT_ID) as Node,
          currentCursorPosition - 1
        );
      }
      limiterValue = 1;
    } else {
      node.setAttribute('limiter', '0');
      node.style.color = theme.palette.text.primary;
    }
  });

  return splitIndex;
};

export const getReadability = (textContent: string): string => {
  if (textContent.trim().length === 0) return '-';
  const score = statsCalculator.fleschScore(textContent)[0];
  let gradLevel = '';
  if (score >= 90) gradLevel = '5th Grade';
  else if (score < 90 && score >= 80) gradLevel = '6th Grade';
  else if (score < 80 && score >= 70) gradLevel = '7th Grade';
  else if (score < 70 && score >= 60) gradLevel = '8th & 9th grade';
  else if (score < 60 && score >= 50) gradLevel = '10th to 12th grade';
  else if (score < 50 && score >= 30) gradLevel = 'College';
  else if (score < 30 && score >= 10) gradLevel = 'College graduate';
  else gradLevel = 'Professional';
  return gradLevel;
};

export const getReadingTime = (numberOfWords: number): string =>
  statsCalculator.readingTime(numberOfWords);

export const getSentiment = (textContent: string): string =>
  statsCalculator.analyzeSentiment(textContent);
